import { useEffect, useState } from 'react';

/**
 * Polling interval for an RTK Query hook that should only poll while the server
 * still owes us something, and should give up rather than poll for the whole
 * session when a server-side pipeline gets stuck.
 *
 * `reason` identifies *what* is being waited on. Changing it restarts the
 * window, so a second upload arriving after the first one stalled still gets a
 * full window of its own — a single latched deadline would leave it unpolled.
 *
 * Returns 0 when polling should be off, which is what RTK treats as "no poll".
 */
export const useCappedPolling = (
    active: boolean,
    reason: string,
    intervalMs: number,
    windowMs: number
): number => {
    const [pollingInterval, setPollingInterval] = useState(0);

    // Deliberately an effect rather than a value computed in render: the cap
    // needs a timer to expire on its own, and reading Date.now() during render
    // makes the interval depend on whether something else happened to re-render.
    useEffect(() => {
        if (!active) {
            setPollingInterval(0);
            return;
        }
        setPollingInterval(intervalMs);
        const giveUp = setTimeout(() => setPollingInterval(0), windowMs);
        return () => clearTimeout(giveUp);
    }, [active, reason, intervalMs, windowMs]);

    return pollingInterval;
};
